import React, { useState, useEffect, useCallback } from 'react';
import { ChefHat, Plus, X, Loader2, ChevronLeft, ChevronRight, Trash2, Edit2, Save } from 'lucide-react';
import api from '../services/api';
import { uyar, hata, onayla } from '../components/GlobalModal';

interface MenuRow {
  menuID: number;
  menuDate: string;
  periodID?: number | null;
  periodName?: string;
  menuName: string;
  description?: string;
  cateringID?: number | null;
  cateringName?: string;
  calories?: number | null;
  isVegetarian?: boolean;
  isActive?: boolean;
}
interface Period { periodID: number; periodName: string; }
interface Catering { cateringID: number; name: string; }

const DAYS = ['PZT', 'SAL', 'ÇAR', 'PER', 'CUM', 'CMT', 'PAZ'];

const monday = (d: Date) => {
  const nd = new Date(d);
  const day = nd.getDay(); // 0=Sun ... 1=Mon
  const diff = day === 0 ? -6 : 1 - day;
  nd.setDate(nd.getDate() + diff);
  nd.setHours(0, 0, 0, 0);
  return nd;
};
// 2026-08-27 (Gonuc: "yemek kaydetmiyor ya da gorunmuyor"):
// Burada toISOString() vardi. toISOString UTC'ye cevirir; Turkiye UTC+3
// oldugu icin YEREL GECE YARISI bir onceki gunun 21:00'ine denk gelir ve
// tarih BIR GUN GERI kayar. Takvim hucreleri 27 Agustos menusunu 28'in
// hucresinde ariyordu — kayit dogruydu, gosterim yanlisti.
// Cozum: UTC'ye hic gitmeden yerel gun/ay/yil bilesenlerini kullan.
const isoDate = (d: Date) => {
  const yil = d.getFullYear();
  const ay = String(d.getMonth() + 1).padStart(2, '0');
  const gun = String(d.getDate()).padStart(2, '0');
  return `${yil}-${ay}-${gun}`;
};
const addDays = (d: Date, n: number) => { const nd = new Date(d); nd.setDate(nd.getDate() + n); return nd; };
const fmtRange = (start: Date) => {
  const end = addDays(start, 6);
  const opt: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short' };
  return `${start.toLocaleDateString('tr-TR', opt)} - ${end.toLocaleDateString('tr-TR', opt)} ${end.getFullYear()}`;
};

const MealMenus: React.FC = () => {
  const [weekStart, setWeekStart] = useState<Date>(monday(new Date()));
  const [tab, setTab] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [menus, setMenus] = useState<MenuRow[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [caterings, setCaterings] = useState<Catering[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<MenuRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    menuDate: isoDate(new Date()),
    periodID: '' as number | '',
    menuName: '',
    description: '',
    cateringID: '' as number | '',
    calories: '' as number | '',
    isVegetarian: false,
    isActive: true,
  });

  const loadMeta = useCallback(async () => {
    try {
      const rP: any = await api.get('/meals/periods');
      const rawP = rP?.data ?? rP;
      setPeriods(Array.isArray(rawP) ? rawP : (Array.isArray(rawP?.data) ? rawP.data : []));
    } catch { setPeriods([]); }
    try {
      const rC: any = await api.get('/meals/catering');
      const rawC = rC?.data ?? rC;
      setCaterings(Array.isArray(rawC) ? rawC : (Array.isArray(rawC?.data) ? rawC.data : []));
    } catch { setCaterings([]); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r: any = await api.get(`/meals/menus/week?date=${isoDate(weekStart)}`);
      const raw = r?.data ?? r;
      const arr = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
      setMenus(arr);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Menüler yüklenemedi');
    } finally { setLoading(false); }
  }, [weekStart]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { load(); }, [load]);

  const openNew = (d?: Date) => {
    setEditing(null);
    setForm({
      menuDate: isoDate(d ?? new Date()),
      periodID: '',
      menuName: '',
      description: '',
      cateringID: '',
      calories: '',
      isVegetarian: false,
      isActive: true,
    });
    setShowModal(true);
  };
  const openEdit = (m: MenuRow) => {
    setEditing(m);
    setForm({
      menuDate: String(m.menuDate).substring(0, 10),
      periodID: m.periodID ?? '',
      menuName: m.menuName ?? '',
      description: m.description ?? '',
      cateringID: m.cateringID ?? '',
      calories: m.calories ?? '',
      isVegetarian: !!m.isVegetarian,
      isActive: m.isActive !== false,
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.menuName.trim()) { uyar('Menü adı zorunlu'); return; }
    setSaving(true); setError(null);
    try {
      const body: any = {
        menuDate: form.menuDate,
        periodID: form.periodID || null,
        menuName: form.menuName,
        description: form.description || null,
        cateringID: form.cateringID || null,
        calories: form.calories === '' ? null : Number(form.calories),
        isVegetarian: form.isVegetarian,
        isActive: form.isActive,
      };
      if (editing) {
        await api.put(`/meals/menus/${editing.menuID}`, body);
      } else {
        await api.post('/meals/menus', body);
      }
      setShowModal(false);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Menü kaydedilemedi');
    } finally { setSaving(false); }
  };

  const remove = async (m: MenuRow) => {
    if (!(await onayla(`"${m.menuName}" silinsin mi?`))) return;
    try {
      await api.delete(`/meals/menus/${m.menuID}`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Silinemedi');
    }
  };

  const menusForDay = (d: Date): MenuRow[] => {
    const iso = isoDate(d);
    return menus.filter(m => String(m.menuDate).substring(0, 10) === iso);
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-800 flex items-center gap-2">
            <ChefHat size={26} className="text-orange-600" /> Yemek Menüleri
          </h1>
          <p className="text-sm text-slate-500 mt-1">Günlük/haftalık/aylık yemek menü planı</p>
        </div>
        <button onClick={() => openNew()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-xl text-sm font-bold hover:bg-orange-700">
          <Plus size={16} /> Yeni Menü
        </button>
      </div>

      <div className="flex items-center gap-2">
        {(['daily', 'weekly', 'monthly'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-full text-sm font-bold ${tab === t ? 'bg-orange-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            {t === 'daily' ? 'Günlük' : t === 'weekly' ? 'Haftalık' : 'Aylık'}
          </button>
        ))}
      </div>

      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}

      <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-2xl p-3 w-fit">
        <button onClick={() => setWeekStart(addDays(weekStart, -7))} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center">
          <ChevronLeft size={16} />
        </button>
        <p className="font-bold text-slate-800 min-w-[220px] text-center">{fmtRange(weekStart)}</p>
        <button onClick={() => setWeekStart(addDays(weekStart, 7))} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center">
          <ChevronRight size={16} />
        </button>
        <button onClick={() => setWeekStart(monday(new Date()))} className="text-xs text-blue-600 hover:underline font-bold ml-2">Bugün</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
        {DAYS.map((label, i) => {
          const d = addDays(weekStart, i);
          const dayMenus = menusForDay(d);
          const isToday = isoDate(d) === isoDate(new Date());
          return (
            <div key={i} className={`bg-white border rounded-xl p-3 min-h-[140px] ${isToday ? 'border-orange-400 border-2' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-[10px] font-black text-slate-500">{label}</p>
                  <p className="text-lg font-black text-slate-800 leading-none">{d.getDate()}</p>
                </div>
                <button onClick={() => openNew(d)} className="w-6 h-6 rounded bg-orange-100 hover:bg-orange-200 text-orange-700 flex items-center justify-center">
                  <Plus size={12} />
                </button>
              </div>
              {loading ? (
                <div className="py-4 text-center"><Loader2 size={14} className="animate-spin mx-auto text-slate-300" /></div>
              ) : dayMenus.length === 0 ? (
                <p className="text-[11px] text-slate-400">—</p>
              ) : (
                <ul className="space-y-2">
                  {dayMenus.map(m => (
                    <li key={m.menuID} className="group text-xs">
                      <p className="font-bold text-slate-800 truncate">{m.menuName}</p>
                      {m.periodName && <p className="text-[10px] text-slate-500">{m.periodName}</p>}
                      <div className="flex items-center gap-1 mt-1 opacity-0 group-hover:opacity-100">
                        <button onClick={() => openEdit(m)} className="text-blue-600 hover:underline text-[10px] inline-flex items-center gap-0.5"><Edit2 size={10} /> Düzenle</button>
                        <button onClick={() => remove(m)} className="text-red-600 hover:underline text-[10px] inline-flex items-center gap-0.5"><Trash2 size={10} /> Sil</button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between bg-orange-600 text-white px-6 py-4 rounded-t-2xl">
              <h3 className="font-black text-lg">{editing ? 'Menü Düzenle' : 'Yeni Menü'}</h3>
              <button onClick={() => setShowModal(false)} className="hover:bg-white/20 rounded p-1"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-3 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Tarih</label>
                  <input type="date" value={form.menuDate} onChange={e => setForm({ ...form, menuDate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Öğün</label>
                  <select value={form.periodID} onChange={e => setForm({ ...form, periodID: e.target.value ? Number(e.target.value) : '' })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                    <option value="">Tüm Gün</option>
                    {periods.map(p => <option key={p.periodID} value={p.periodID}>{p.periodName}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Menü Adı *</label>
                <input type="text" value={form.menuName} onChange={e => setForm({ ...form, menuName: e.target.value })}
                  placeholder="Mercimek Çorbası, Tavuk Sote, Pilav, Salata"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Açıklama</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Yemek Firması</label>
                  <select value={form.cateringID} onChange={e => setForm({ ...form, cateringID: e.target.value ? Number(e.target.value) : '' })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                    <option value="">Seçiniz</option>
                    {caterings.map(c => <option key={c.cateringID} value={c.cateringID}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Kalori</label>
                  <input type="number" value={form.calories} onChange={e => setForm({ ...form, calories: e.target.value === '' ? '' : Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={form.isVegetarian} onChange={e => setForm({ ...form, isVegetarian: e.target.checked })} className="w-4 h-4" />
                  Vejetaryen
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} className="w-4 h-4" />
                  Aktif
                </label>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-100 p-4">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-bold text-slate-700 hover:bg-slate-50">İptal</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-bold hover:bg-orange-700 disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MealMenus;
